import { useState } from 'react';
import { useWebSocket } from '@/context/WebSocketContext';
import { Bell, X, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { canUsePush } from '@/lib/alarm';

export function PushBanner() {
  const { pushSupported, pushEnabled, enablePush } = useWebSocket();
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('push_banner_dismissed') === '1');
  const [loading, setLoading] = useState(false);

  // If push is already enabled or dismissed, hide
  if (pushEnabled || dismissed) return null;

  // iOS in Safari tab (not standalone) — show "add to home screen" message
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;

  if (isIOS && !isStandalone) {
    return (
      <div className="bg-navy text-white px-4 py-3 flex items-center justify-between gap-3" data-testid="push-banner-ios">
        <div className="flex items-center gap-3 min-w-0">
          <Smartphone className="h-5 w-5 shrink-0" />
          <p className="text-sm font-ibm">
            <strong>Lägg till på hemskärmen</strong> för att få push-notiser.
            Tryck <span className="inline-block px-1.5 py-0.5 bg-white/20 rounded text-xs mx-1">Dela</span> → <strong>Lägg till på hemskärmen</strong>
          </p>
        </div>
        <button onClick={() => { setDismissed(true); localStorage.setItem('push_banner_dismissed', '1'); }}
          className="text-white/60 hover:text-white p-1 shrink-0" data-testid="push-dismiss-btn">
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // Push not supported at all
  if (!pushSupported && !canUsePush()) return null;

  const handleEnable = async () => {
    setLoading(true);
    const ok = await enablePush();
    setLoading(false);
    if (ok) {
      toast.success('Push-notiser aktiverade! Du får nu larm även när appen är stängd.');
    } else {
      toast.error('Kunde inte aktivera. Kontrollera att du tillåter notiser i inställningarna.');
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('push_banner_dismissed', '1');
  };

  return (
    <div className="bg-navy text-white px-4 py-3 flex items-center justify-between gap-3" data-testid="push-banner">
      <div className="flex items-center gap-3 min-w-0">
        <Bell className="h-5 w-5 shrink-0 animate-pulse" />
        <p className="text-sm font-ibm truncate">
          Aktivera push-notiser för att få larm även när appen är stängd
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={handleEnable} disabled={loading}
          className="bg-white text-navy px-4 py-1.5 rounded-sm text-sm font-ibm font-bold hover:bg-slate-100 transition-colors"
          data-testid="push-enable-btn">
          {loading ? 'Aktiverar...' : 'Aktivera'}
        </button>
        <button onClick={handleDismiss}
          className="text-white/60 hover:text-white p-1" data-testid="push-dismiss-btn">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
